import type { StoreApi } from "zustand";
import type { AttributeCollectionId } from "../../types";
import { parseFieldId } from "../../utils/field-id";
import type {
  DynamicCollectionActions,
  FormStore,
  SliceSetFunction,
} from "../types";

/**
 * 動的コレクション機能スライス
 *
 * 主な責務:
 * - コレクションインデックスの取得
 * - コレクションの追加・削除
 * - コレクション関連の状態管理
 */
export const createDynamicCollectionSlice = (
  set: SliceSetFunction,
  get: () => FormStore,
  _store: StoreApi<FormStore>,
): DynamicCollectionActions => ({
  /**
   * コレクションのインデックス配列を取得する
   * @param collectionId コレクションID
   * @returns インデックス配列
   */
  getCollectionIndices: (collectionId: AttributeCollectionId): number[] => {
    const state = get();

    // フォーム定義からコレクションインデックスを取得
    const definitionIndices: number[] = [];
    if (state.form?.form?.attributeCollections) {
      state.form.form.attributeCollections.forEach((collection, index) => {
        if (collection.id === collectionId) {
          definitionIndices.push(index);
        }
      });
    }

    // 値のデータからコレクションインデックスを抽出
    const valueIndices = new Set<number>();
    state.values.forEach((value) => {
      const parsedId = parseFieldId(value.fieldId);
      if (parsedId && parsedId.collectionId === collectionId) {
        valueIndices.add(parsedId.collectionIndex);
      }
    });

    // 両方をマージして重複を除去
    const allIndices = [...definitionIndices, ...valueIndices];
    const uniqueIndices = [...new Set(allIndices)].sort((a, b) => a - b);

    // フォーム定義からもvalueからもコレクションが見つからない場合は
    // 最低でも0番のインデックスを含める（空のフォームを表示するため）
    if (uniqueIndices.length === 0 && state.values.length > 0) {
      return [0];
    }

    return uniqueIndices;
  },

  /**
   * コレクションを追加する
   * @param collectionId コレクションID
   * @returns 追加されたコレクションのインデックス
   */
  addCollection: (collectionId: AttributeCollectionId): number => {
    const state = get();

    // 現在のコレクションインデックスを取得（自己参照を避けるため、ロジックを直接記述）
    const currentIndices: number[] = [];

    // フォーム定義からコレクションインデックスを取得
    if (state.form?.form?.attributeCollections) {
      state.form.form.attributeCollections.forEach((collection, index) => {
        if (collection.id === collectionId) {
          currentIndices.push(index);
        }
      });
    }

    // 値のデータからコレクションインデックスを抽出
    state.values.forEach((value) => {
      const parsedId = parseFieldId(value.fieldId);
      if (parsedId && parsedId.collectionId === collectionId) {
        currentIndices.push(parsedId.collectionIndex);
      }
    });

    // 重複を除去してソート
    const uniqueIndices = [...new Set(currentIndices)].sort((a, b) => a - b);

    // 次のインデックスを計算（配列の最大値 + 1）
    const nextIndex =
      uniqueIndices.length > 0 ? Math.max(...uniqueIndices) + 1 : 0;

    // コレクションが追加されたとき、必ず有効状態で始まるように設定
    if (state.setCollectionEnabled) {
      state.setCollectionEnabled(collectionId, nextIndex, true);
    }

    return nextIndex;
  },

  /**
   * コレクションを削除する
   * @param collectionId コレクションID
   * @param collectionIndex コレクションインデックス
   */
  removeCollection: (
    collectionId: AttributeCollectionId,
    collectionIndex: number,
  ): void => {
    // このコレクションに関連する値を削除
    set(
      (state) => {
        // フィールド値を削除
        const newValues = state.values.filter((value) => {
          const parsedId = parseFieldId(value.fieldId);
          return !(
            parsedId &&
            parsedId.collectionId === collectionId &&
            parsedId.collectionIndex === collectionIndex
          );
        });

        // エラーを削除
        const newErrors = state.errors.filter((error) => {
          const parsedId = parseFieldId(error.fieldId);
          return !(
            parsedId &&
            parsedId.collectionId === collectionId &&
            parsedId.collectionIndex === collectionIndex
          );
        });

        // 無効化リストからも削除
        const newDisabledCollections = state.disabledCollections.filter(
          (item) =>
            !(
              item.collectionId === collectionId &&
              item.collectionIndex === collectionIndex
            ),
        );

        return {
          values: newValues,
          errors: newErrors,
          disabledCollections: newDisabledCollections,
        };
      },
      false,
      "dynamicCollection/removeCollection",
    );
  },
});
